import React from 'react';
import { Contract, Mpid, ThirdPartyContact } from '../../types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { EHttpMethod, usePortalQuery } from '../../hooks/usePortalQuery';
import { Button } from '@portal_components/components/Button';
import { Dropdown } from '@portal_components/components/Dropdown';
import { FormCard } from '@portal_components/components/FormCard';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { TableHeadings } from '@portal_components/components/TableHeadings';
import { TableItem } from '@portal_components/components/TableItem';

interface MpidTabProps {
  readonly: boolean;
}

export const MpidsTab = ({ readonly }: MpidTabProps) => {
  const { getValues, setValue, watch } = useFormContext<Contract>();
  const [selectedSupplierMpid, setSelectedSupplierMpid] = React.useState<string | undefined>(undefined);
  const { fields, append, remove } = useFieldArray<Contract, 'mpidList'>({ name: 'mpidList' });

  const { data: industryMpidList, isLoading: isLoadingIndustryMpidList } = usePortalQuery<
    ThirdPartyContact[]
  >(
    EHttpMethod.GET,
    'industry-mpids',
    'get-industry-mpids',
    true,
    undefined,
    undefined,
    undefined,
    false,
    600000, // ensure data is not stale for 10 minutes
    600000 // prevent data being garbage collected for 10 minutes
  );

  const handleRemoveMpid = (index: number) => () => {
    if (getValues(`mpidList.${index}.contractMpidEfd`) && getValues(`mpidList.${index}.contractMpidEtd`)) {
      const markedForDeletion = getValues(`mpidList.${index}.markedForDeletion`);
      setValue(`mpidList.${index}.markedForDeletion`, !markedForDeletion);
      return;
    }
    remove(index);
  };

  const mpidsTableItem = (_: Mpid, index: number) => {
    const markedForDeletion = watch(`mpidList.${index}.markedForDeletion`);
    return (
      <div className="flex flex-col w-full pl-2" key={`ServiceStandardPrice-${index}`}>
        {markedForDeletion && <p className="text-feedback-orange text-sm">MPID marked for deletion</p>}
        <TableItem key={`mpid-${index}`} remove={handleRemoveMpid(index)} readonly={readonly}>
          <FormTextInput<Contract>
            title="MPID"
            name={`mpidList.${index}.contact.industryMpid`}
            readonly
            mandatory
          />
          <FormTextInput<Contract>
            title="Name"
            name={`mpidList.${index}.contact.contactName`}
            readonly={readonly || markedForDeletion}
          />
          <FormTextInput<Contract>
            title="Phone Number"
            name={`mpidList.${index}.contact.phoneNumber`}
            readonly={readonly || markedForDeletion}
          />
          <FormTextInput<Contract>
            title="Email Address"
            name={`mpidList.${index}.contact.emailAddress`}
            readonly={readonly || markedForDeletion}
          />
          <FormTextInput<Contract>
            title="Contact Description"
            name={`mpidList.${index}.contact.contactDescription`}
            readonly={readonly || markedForDeletion}
            mandatory
          />
        </TableItem>
      </div>
    );
  };

  const handleAddMpidButtonClick = () => {
    if (selectedSupplierMpid === undefined) return;
    if (!industryMpidList) return;
    const mpidData = industryMpidList.find(mpid => mpid.industryMpid === selectedSupplierMpid);
    if (!mpidData) return;
    append({ contact: mpidData, tpContactId: mpidData.tpContactId });
  };

  return (
    <div className="flex flex-col w-full">
      <FormCard title="MPIDs">
        {!readonly && isLoadingIndustryMpidList && <p>Loading MPIDs...</p>}
        {!readonly && !isLoadingIndustryMpidList && (
          <div className="grid grid-cols-2 gap-2 items-start">
            <Dropdown
              title="MPID"
              placeholder="Select..."
              elementsForDisplay={(industryMpidList ?? []).map(mpid => mpid.industryMpid)}
              onSelect={(_, value) => setSelectedSupplierMpid(value)}
            />
            <div className="mt-3">
              <Button
                text="Add MPID"
                disabled={!selectedSupplierMpid}
                primary
                onClick={handleAddMpidButtonClick}
              />
            </div>
          </div>
        )}
        <TableHeadings headings={['MPID', 'Name', 'Phone Number', 'Email Address', 'Contact Description']} />
        {fields.map(mpidsTableItem)}
      </FormCard>
    </div>
  );
};
